/**
 * "Que dia é hoje" — no fuso da INSTALAÇÃO, não no do navegador.
 *
 * A tela de consumo de IA conta o dia em Brasília fixo no servidor
 * (decisão da dona, 2026-09-11: por enquanto uma instalação só, e o
 * custo de trocar depois é baixo — é UMA constante de cada lado). O
 * atalho "últimos 7 dias" precisa começar do MESMO hoje que o servidor
 * usa pra fechar o dia; se começasse do hoje do navegador, quem olha
 * de Lisboa às 01:00 pediria um dia que em Brasília ainda não começou,
 * e o servidor devolveria zero com cara de "não houve consumo".
 *
 * **Sem `Date` de meia-noite em lugar nenhum**: tudo aqui é texto
 * `AAAA-MM-DD`, e a aritmética de dias é feita em UTC sobre esse
 * texto — `new Date("2026-09-01")` é meia-noite UTC por especificação,
 * então somar dias em UTC nunca cruza horário de verão de ninguém. É
 * o mesmo cuidado de `date-range.ts` (os dígitos são o relógio), e a
 * suíte roda nos 6 fusos pra provar.
 */

/** Espelho de `web_api.ports.INSTALLATION_TIME_ZONE`. A resposta do
 *  servidor também traz `timezone`, e a tela CONFERE os dois — se um
 *  dia divergirem, o aviso aparece em vez de a conta sair torta. */
export const INSTALLATION_TIME_ZONE = "America/Sao_Paulo";

const diaLocal = new Intl.DateTimeFormat("en-CA", {
  timeZone: INSTALLATION_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});

/** `AAAA-MM-DD` de AGORA no fuso da instalação. `en-CA` é o único
 *  locale cujo formato numérico curto já é ISO — sem montar string à
 *  mão a partir de `formatToParts`. */
export function todayInInstallation(now: Date = new Date()): string {
  return diaLocal.format(now);
}

/** `iso ± dias`, em texto, sem tocar em fuso. */
export function shiftDays(iso: string, days: number): string {
  const base = new Date(`${iso}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

/** Primeiro dia do mês de `iso`. */
export function startOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

/** Último dia do mês de `iso`: o dia zero do mês seguinte, em UTC. */
export function endOfMonth(iso: string): string {
  const [ano, mes] = iso.split("-").map(Number);
  return new Date(Date.UTC(ano, mes, 0)).toISOString().slice(0, 10);
}

export const PERIOD_PRESETS = ["last7", "last30", "thisMonth", "lastMonth", "custom"] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  last7: "Últimos 7 dias",
  last30: "Últimos 30 dias",
  thisMonth: "Este mês",
  lastMonth: "Mês passado",
  custom: "Personalizado"
};

export type DayWindow = { from: string; to: string };

/** A janela de cada atalho, contada a partir do hoje da instalação.
 *  "Últimos 7 dias" INCLUI hoje (hoje e os seis anteriores) — é o que
 *  a pessoa espera ao abrir a tela de manhã querendo ver ontem. */
export function presetWindow(preset: Exclude<PeriodPreset, "custom">, today = todayInInstallation()): DayWindow {
  switch (preset) {
    case "last7":
      return { from: shiftDays(today, -6), to: today };
    case "last30":
      return { from: shiftDays(today, -29), to: today };
    case "thisMonth":
      return { from: startOfMonth(today), to: today };
    case "lastMonth": {
      const fimDoAnterior = shiftDays(startOfMonth(today), -1);
      return { from: startOfMonth(fimDoAnterior), to: endOfMonth(fimDoAnterior) };
    }
  }
}
